import { useState } from "react";
import { DetailCard } from "src/components/Details/Details";
import { TbFileEuro } from "react-icons/tb";
import Dunning from "src/components/Dunning/Dunning";
import RemainingAmount from "src/components/RemainingAmount/RemainingAmount";
import InvoiceStatus from "src/components/InvoiceStatus/InvoiceStatus";
import { useTranslation } from "react-i18next";
import { Invoice } from "src/types";

const STATUS_OPTIONS = [
    { value: -1,  labelKey: "filters.all" },
    { value: 0,   labelKey: "filters.status.open",       modifier: "grey"   },
    { value: 2,   labelKey: "filters.status.paid",       modifier: "green"  },
    { value: 4,   labelKey: "filters.status.overdue",    modifier: "red"    },
    { value: 5,   labelKey: "filters.status.writtenOff", modifier: "purple" },
];

interface RecipientInvoicesCardProps {
    invoices: Invoice[];
    navigate: (path: string) => void;
}

export function RecipientInvoicesCard({ invoices, navigate }: RecipientInvoicesCardProps) {
    const { t } = useTranslation();
    const [statusFilter, setStatusFilter] = useState(-1);

    const filtered = statusFilter === -1
        ? invoices
        : invoices.filter((inv) => inv.status === statusFilter);

    return (
        <DetailCard title={<><TbFileEuro /> {t("nav.invoices")} ({invoices.length})</>} full>
            <div className="inv-filter__row inv-filter__row--wrap" style={{ padding: "8px 0 12px" }}>
                <span className="inv-filter__label">{t("table.status")}</span>
                <div className="inv-filter__chips">
                    {STATUS_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            className={
                                opt.modifier
                                    ? `invoice-status invoice-status--${opt.modifier} inv-filter__chip-badge${statusFilter === opt.value ? " inv-filter__chip-badge--active" : ""}`
                                    : `inv-filter__chip${statusFilter === opt.value ? " inv-filter__chip--active" : ""}`
                            }
                            onClick={() => setStatusFilter(statusFilter === opt.value ? -1 : opt.value)}
                        >
                            {t(opt.labelKey)}
                        </button>
                    ))}
                </div>
            </div>

            {!filtered.length ? (
                <div className="empty-state">{t("invoices.noFound")}</div>
            ) : (
                <table className="dashboard-table">
                    <thead>
                        <tr>
                            <th>{t("table.id")}</th>
                            <th>{t("table.patient")}</th>
                            <th>{t("table.status")}</th>
                            <th>{t("table.amount")}</th>
                            <th>{t("table.dunning")}</th>
                            <th>{t("table.dates")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((p) => {
                            const isOverdue = p.status === 4;
                            const isPaid    = p.status === 2;
                            const duePast   = !isPaid && p.dueDate && new Date(p.dueDate) < new Date();
                            // Mirrors InvoiceRow.tsx / InvoiceAmountsCard.tsx's canonical total: net
                            // includes materialCosts (Auslagen), preferring the backend's precomputed
                            // vatAmount (correct even when lines carry different VAT rates) over the
                            // flat net*rate fallback, plus any dunning fine.
                            const vr    = p.vatRate ?? 0;
                            const net   = (p.amount ?? 0) + (p.materialCosts ?? 0);
                            const vatAmount = p.vatAmount ?? (vr > 0 ? +(net * vr / 100).toFixed(2) : 0);
                            const gross = +(net + vatAmount + (p.dunningFineAmount ?? 0)).toFixed(2);
                            return (
                                <tr
                                    key={p.id}
                                    className={`clickable-row${isOverdue ? " inv-row--overdue" : ""}`}
                                    onClick={() => navigate(`/invoices/${p.id}`)}
                                >
                                    <td style={{ fontWeight: 600 }}>{p.readableId}</td>
                                    <td>
                                        {p.patient
                                            ? `${p.patient.firstName} ${p.patient.lastName}`.trim()
                                            : "-"}
                                    </td>
                                    <td><InvoiceStatus status={p.status} unsent={!p.sentDate} /></td>
                                    <td>
                                        <div className="inv-amount-col">
                                            <span className="inv-amount-col__total">€ {gross.toFixed(2)}</span>
                                            {!isPaid && <RemainingAmount amount={gross} paidAmount={p.paidAmount} />}
                                        </div>
                                    </td>
                                    <td>
                                        <Dunning level={p.dunning} />
                                    </td>
                                    <td>
                                        <div className="inv-date-chain">
                                            <span>{new Date(p.creationDate).toLocaleDateString()}</span>
                                            <span className="inv-date-chain__sep">→</span>
                                            <span className={!p.sentDate ? "inv-date-chain__unsent" : ""}>
                                                {p.sentDate ? new Date(p.sentDate).toLocaleDateString() : t("filters.notSent")}
                                            </span>
                                            <span className="inv-date-chain__sep">→</span>
                                            <span className={duePast ? "inv-date-chain__overdue" : ""}>
                                                {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : "–"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </DetailCard>
    );
}
